import { createContext, useContext, useState } from 'react';
import { calculateWindow } from '../calculations/index';

const JobContext = createContext(null);

const defaultJob = {
  customerName: '',
  jobName:      '',
  woNumber:     '',
  orderDate:    new Date().toISOString().split('T')[0],
  color:        'White',
  windows:      [],
};

function makeWindow(defaultColor) {
  // ── Grid color safety default ────────────────────────────────────────────
  // If the frame is anything OTHER than 'White', the grids should match the
  // frame — otherwise Tracy Corners ships White grids inside a dark window,
  // which is a costly mismatch. Operator can override on the pane, but the
  // default won't be wrong out of the gate.
  const frame = defaultColor || 'White';
  const autoGridColor = (frame && frame !== 'White') ? frame : '';

  return {
    id:            Date.now() + Math.random(),
    type:          'DH',
    label:         '',
    qty:           1,
    width:         '',
    height:        '',
    color:         frame,
    config:        'Single',
    handing:       'Hinge Left',
    finType:       'With Fin',
    glassType:     'DS',
    glassConfig:   'DLA',
    gasFill:       'Argon',
    grids:         false,
    gridPattern:   '',
    gridQtyV:      0,
    gridQtyH:      0,
    gridColor:     autoGridColor,
    screen:        false,
    screenSize:    'Full',
    meshType:      'Plain',
    screenType:    'Flex Scrn',
    options:       '',
    mullCount:     0,
    mullType:      '',
    threeHSLayout: 'qhq',   // 3-Lite Horizontal Slider: 'qhq' | 'thirds'
  };
}

// ── Dimension parser ──────────────────────────────────────────────────────────
function parseDimension(val) {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return isNaN(val) ? null : val;
  const str = String(val).trim();
  if (!str) return null;
  if (/^\d+(\.\d+)?$/.test(str)) return parseFloat(str);
  if (/^\d+\/\d+$/.test(str)) {
    const [n, d] = str.split('/');
    return parseInt(n) / parseInt(d);
  }
  const mixed = str.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) return parseInt(mixed[1]) + parseInt(mixed[2]) / parseInt(mixed[3]);
  const n = parseFloat(str);
  return isNaN(n) ? null : n;
}

// ── Run calculations ──────────────────────────────────────────────────────────
function runCalculations(windows, woNumber) {
  const errs    = [];
  const results = [];

  windows.forEach((w, i) => {
    const parsedW = parseDimension(w.width);
    const parsedH = parseDimension(w.height);

    if (parsedW === null || parsedH === null || parsedW <= 0 || parsedH <= 0) {
      errs.push(`Window ${i + 1}: width and height are required.`);
      return;
    }

    try {
      const label  = w.label || `${woNumber || 'W'}-${i + 1}`;
      const result = calculateWindow({
        ...w,
        label,
        width:    parsedW,
        overallW: parsedW,
        height:   parsedH,
      });

      const finalOverallW = result.overallW ?? parsedW;
      const finalHeight   = result.height   ?? parsedH;

      results.push({
        ...result,
        overallW:      finalOverallW,
        width:         finalOverallW,
        height:        finalHeight,
        qty:           w.qty           ?? 1,
        finType:       w.finType       ?? result.finType     ?? '',
        screenType:    w.screenType    ?? result.screenType  ?? '',
        screenSize:    w.screenSize    ?? result.screenSize  ?? '',
        meshType:      w.meshType      ?? result.meshType    ?? '',
        options:       w.options       ?? '',
        grids:         w.grids         ?? false,
        gridPattern:   w.gridPattern   ?? '',
        gridQtyV:      w.gridQtyV      ?? 0,
        gridQtyH:      w.gridQtyH      ?? 0,
        gridColor:     w.gridColor     ?? '',
        mullCount:     w.mullCount     ?? 0,
        mullType:      w.mullType      ?? '',
        color:         w.color         ?? result.color       ?? 'White',
        config:        w.config        ?? result.config      ?? 'Single',
        type:          w.type          ?? result.type        ?? 'DH',
        handing:       w.handing       ?? result.handing     ?? 'Hinge Left',
        glassType:     w.glassType     ?? result.glassType   ?? 'DS',
        glassConfig:   w.glassConfig   ?? result.glassConfig ?? 'DLA',
        gasFill:       w.gasFill       ?? result.gasFill     ?? 'Argon',
        threeHSLayout: w.threeHSLayout ?? result.threeHSLayout ?? 'qhq',
      });
    } catch (e) {
      errs.push(`Window ${i + 1}: ${e.message}`);
    }
  });

  return { results, errs };
}

// ── Reconstruct editable windows from calculatedWindows ───────────────────────
function reconstructWindowsFromCalculated(calculatedWindows) {
  return calculatedWindows.map((cw, i) => ({
    id:            Date.now() + Math.random() + i,
    type:          cw.type          || 'DH',
    label:         cw.label         || '',
    qty:           cw.qty           ?? 1,
    width:         cw.overallW      ?? cw.width ?? '',
    height:        cw.height        ?? '',
    color:         cw.color         || 'White',
    config:        cw.config        || 'Single',
    handing:       cw.handing       || 'Hinge Left',
    finType:       cw.finType       || 'With Fin',
    glassType:     cw.glassType     || 'DS',
    glassConfig:   cw.glassConfig   || 'DLA',
    gasFill:       cw.gasFill       || 'Argon',
    grids:         cw.grids         ?? false,
    gridPattern:   cw.gridPattern   || '',
    gridQtyV:      cw.gridQtyV      ?? 0,
    gridQtyH:      cw.gridQtyH      ?? 0,
    gridColor:     cw.gridColor     || '',
    screen:        cw.screen        ?? false,
    screenSize:    cw.screenSize    || 'Full',
    meshType:      cw.meshType      || 'Plain',
    screenType:    cw.screenType    || 'Flex Scrn',
    options:       cw.options       || '',
    mullCount:     cw.mullCount     ?? 0,
    mullType:      cw.mullType      || '',
    threeHSLayout: cw.threeHSLayout || 'qhq',
  }));
}

export function JobProvider({ children }) {
  const [job, setJob]                             = useState(defaultJob);
  const [calculatedWindows, setCalculatedWindows] = useState([]);
  const [errors, setErrors]                       = useState([]);
  const [linkedJobId, setLinkedJobId]             = useState(null);

  function updateJob(field, value) {
    setJob(prev => ({ ...prev, [field]: value }));
  }

  function addWindow() {
    setJob(prev => ({
      ...prev,
      windows: [...prev.windows, makeWindow(prev.color)],
    }));
  }

  function updateWindow(id, field, value) {
    setJob(prev => ({
      ...prev,
      windows: prev.windows.map(w =>
        w.id === id ? { ...w, [field]: value } : w
      ),
    }));
  }

  function removeWindow(id) {
    setJob(prev => ({
      ...prev,
      windows: prev.windows.filter(w => w.id !== id),
    }));
  }

  function duplicateWindow(id) {
    setJob(prev => {
      const idx = prev.windows.findIndex(w => w.id === id);
      if (idx === -1) return prev;
      const copy = { ...prev.windows[idx], id: Date.now() + Math.random() };
      const next = [...prev.windows];
      next.splice(idx + 1, 0, copy);
      return { ...prev, windows: next };
    });
  }

  function calculateAll() {
    const { results, errs } = runCalculations(job.windows, job.woNumber);
    setErrors(errs);
    if (results.length > 0) setCalculatedWindows(results);
    return results;
  }

  function loadFromJob(firebaseJob) {
    if (!firebaseJob) return;

    const hasWindows    = Array.isArray(firebaseJob.windows)           && firebaseJob.windows.length > 0;
    const hasCalculated = Array.isArray(firebaseJob.calculatedWindows) && firebaseJob.calculatedWindows.length > 0;

    let editableWindows = [];
    if (hasWindows) {
      editableWindows = firebaseJob.windows;
    } else if (hasCalculated) {
      editableWindows = reconstructWindowsFromCalculated(firebaseJob.calculatedWindows);
    }

    const restored = {
      customerName: firebaseJob.customerName || '',
      jobName:      firebaseJob.jobName      || '',
      woNumber:     firebaseJob.woNumber     || '',
      orderDate:    firebaseJob.orderDate    || firebaseJob.contactDate || new Date().toISOString().split('T')[0],
      color:        firebaseJob.color        || 'White',
      windows:      editableWindows,
    };

    setJob(restored);
    setLinkedJobId(firebaseJob.id || null);
    setErrors([]);

    if (hasCalculated) {
      setCalculatedWindows(firebaseJob.calculatedWindows);
    } else if (hasWindows) {
      const { results, errs } = runCalculations(firebaseJob.windows, firebaseJob.woNumber);
      if (results.length > 0) setCalculatedWindows(results);
      if (errs.length > 0)    setErrors(errs);
    } else {
      setCalculatedWindows([]);
    }
  }

  function clearJob() {
    setJob(defaultJob);
    setCalculatedWindows([]);
    setErrors([]);
    setLinkedJobId(null);
  }

  return (
    <JobContext.Provider value={{
      job,
      calculatedWindows,
      errors,
      linkedJobId,
      updateJob,
      loadFromJob,
      addWindow,
      updateWindow,
      removeWindow,
      duplicateWindow,
      calculateAll,
      clearJob,
    }}>
      {children}
    </JobContext.Provider>
  );
}

export function useJobContext() {
  const ctx = useContext(JobContext);
  if (!ctx) throw new Error('useJobContext must be used inside <JobProvider>');
  return ctx;
}